<template>
    <ckeditor ref="editor" :editor="editor" v-model="value" :config="editorConfig" @ready="onReady"></ckeditor>
</template>

<script>
import CKEditor from "@ckeditor/ckeditor5-vue";
import FullBuildClassicEditor from 'custom-ckeditor5';
import 'custom-ckeditor5/build/translations/tr';
import UploadAdapter from "@/core/plugins/upload-adapter";
import DeferredUploadAdapter from "@/core/plugins/deferred-upload-adapter";

export default {
    name: "index",
    props: ['model', 'enterToBr', 'basicEditor', 'deferUpload', 'uploadEnabled'],
    components: {
        ckeditor: CKEditor.component
    },
    data() {
        return {
            editorInstance: null,
            editor: FullBuildClassicEditor,
            editorConfig: {
                toolbar: {
                    items: [
                        'heading',
                        '|',
                        'fontSize',
                        'fontFamily',
                        'fontColor',
                        'fontBackgroundColor',
                        'imageInsert',
                        '|',
                        'bold',
                        'italic',
                        'underline',
                        'strikethrough',
                        'highlight',
                        'removeFormat',
                        '|',
                        'alignment',
                        '|',
                        'numberedList',
                        'bulletedList',
                        '|',
                        'indent',
                        'outdent',
                        '|',
                        'todoList',
                        'link',
                        'blockQuote',
                        'imageUpload',
                        'insertTable',
                        'mediaEmbed',
                        'htmlEmbed',
                        '|',
                        'undo',
                        'redo',
                        '|',
                        'math',
                        '|',
                        'sourceEditing'
                    ],
                    shouldNotGroupWhenFull: false
                },
                language: 'tr',
                image: {
                    toolbar: [
                        'imageTextAlternative',
                        '|',
                        'imageStyle:block',
                        'imageStyle:side',
                        '|',
                        'linkImage'
                    ]
                },
                table: {
                    contentToolbar: [
                        'tableColumn',
                        'tableRow',
                        'mergeTableCells'
                    ]
                },
                htmlSupport: {
                    allow: [
                        {
                            name: /.*/,
                            attributes: true,
                            classes: true,
                            styles: true
                        }
                    ],
                    disallow: [
                        { name: /.*/, classes: [ /^todo-list/ ] },
                        //{ name: 'input' }
                    ],
                },
                mediaEmbed: {
                    previewsInData: true,
                    extraProviders: [
                        {
                            // hint: this is just for previews. Get actual HTML codes by making API calls from your CMS
                            name: 'Support Mp4',

                            // Match all URLs or just the ones you need:
                            url: /^(.*)\.mp4$/,

                            html: match => {
                                return ('<video controls="" name="media"><source src="'+match[0]+'" type="video/mp4"></video>');
                            }
                        },
                        {
                            // hint: this is just for previews. Get actual HTML codes by making API calls from your CMS
                            name: 'Iframe',

                            // Match all URLs or just the ones you need:
                            url: /^iframe:(.*)$/,

                            html: match => {
                                try {
                                    let url = new URL(match[1]);
                                    let foundAttrs = [];

                                    ['width', 'height'].forEach((attr) => {
                                        if(url.searchParams.get(attr)){
                                            foundAttrs.push(this.sprintf('%s="%s"', [attr, url.searchParams.get(attr)]));
                                        }
                                    });

                                    return (this.sprintf('<iframe src="%s" %s></iframe>', [url, foundAttrs.join(' ')]));
                                } catch (e) {
                                    return ('Iframe URL Error');
                                }
                            }
                        }
                    ]
                },
                math: {
                    engine: 'katex', // or katex or function. E.g. (equation, element, display) => { ... }
                    lazyLoad: undefined, // async () => { ... }, called once before rendering first equation if engine doesn't exist. After resolving promise, plugin renders equations.
                    outputType: 'span', // or span
                    forceOutputType: false, // forces output to use outputType
                    enablePreview: true, // Enable preview view
                    previewClassName: [], // Class names to add to previews
                    popupClassName: [], // Class names to add to math popup balloon
                },
                fontSize: {
                    options: [
                        6,
                        8,
                        10,
                        'tiny',
                        12,
                        'small',
                        14,
                        'default',
                        16,
                        18,
                        20,
                        'big',
                        24,
                        28,
                        'huge',
                        32,
                        36,
                        48
                    ]
                },
                heading: {
                    options: [
                        { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                        { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                        { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                        { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                        { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
                        { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
                        { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
                    ]
                },
                licenseKey: ''
            }
        }
    },
    created(){
        if(this.basicEditor) {
            this.editorConfig.toolbar.items = [
                'bold',
                'italic',
                'fontColor',
                '|',
                'undo',
                'redo',
            ]
        }
    },
    computed:{
        value: {
            get() {
                return this.model != null ? this.model : undefined
            },
            set(val) {
                this.$emit('updateModel', val)
            }
        },
    },
    watch: {
        uploadEnabled(val) {
            if (!this.editorInstance) return
            if (val === false) {
                this._disableImageCommands(this.editorInstance)
            } else {
                this._enableImageCommands(this.editorInstance)
            }
        },
    },
    methods: {
        getData() {
            return this.editorInstance ? this.editorInstance.getData() : this.model
        },
        onReady(editor)  {
            this.editorInstance = editor;

            editor.ui.getEditableElement().parentElement.insertBefore(
                editor.ui.view.toolbar.element,
                editor.ui.getEditableElement()
            );

            if (this.deferUpload) {
                this._pendingUploads = new Map()
                editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
                    return new DeferredUploadAdapter(loader, this._pendingUploads)
                }
            } else {
                editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
                    return new UploadAdapter(loader);
                }
            }

            if (this.uploadEnabled === false) {
                this._disableImageCommands(editor)
            }

            if(this.enterToBr){
                editor.editing.view.document.on('enter', ( evt, data ) => {
                    editor.execute('shiftEnter');
                    data.preventDefault();
                    evt.stop();
                }, {priority: 'high' });
            }

            editor.model.document.on('change:data', () => {
                this.$emit('liveUpdate', editor.getData())
            })
        },
        _disableImageCommands(editor) {
            const cmds = ['imageUpload', 'imageInsert', 'uploadImage']
            for (const name of cmds) {
                const cmd = editor.commands.get(name)
                if (cmd) cmd.forceDisabled('uploadDisabled')
            }
        },
        _enableImageCommands(editor) {
            const cmds = ['imageUpload', 'imageInsert', 'uploadImage']
            for (const name of cmds) {
                const cmd = editor.commands.get(name)
                if (cmd) cmd.clearForceDisabled('uploadDisabled')
            }
        },
        getPendingUploads() {
            if (!this._pendingUploads || this._pendingUploads.size === 0) return []
            const content = this.editorInstance ? this.editorInstance.getData() : ''
            const result = []
            for (const [blobUrl, file] of this._pendingUploads.entries()) {
                if (content.includes(blobUrl)) {
                    result.push({blobUrl, file})
                } else {
                    URL.revokeObjectURL(blobUrl)
                    this._pendingUploads.delete(blobUrl)
                }
            }
            return result
        },
        clearPendingUploads() {
            if (!this._pendingUploads) return
            for (const blobUrl of this._pendingUploads.keys()) {
                URL.revokeObjectURL(blobUrl)
            }
            this._pendingUploads.clear()
        },
        hasPendingUploads() {
            return this._pendingUploads ? this._pendingUploads.size > 0 : false
        },
    }
}
</script>

<style>
.ck .ck-splitbutton {
    display: flex;
}
.ck-body-wrapper .ck-dropdown__panel {
    z-index: 100400 !important;
}
.ck .ck-content {
    max-height: 800px;
}
.ck .ck-source-editing-area {
    max-height: 800px;
}
.ck .ck-source-editing-area textarea{
    max-height: 800px;
    overflow-y: auto;
}

/* width */
.ck .ck-source-editing-area textarea::-webkit-scrollbar {
    width: 5px;
}

/* Track */
.ck .ck-source-editing-area textarea::-webkit-scrollbar-track {
    background: transparent;
}

/* Handle */
.ck .ck-source-editing-area textarea:hover::-webkit-scrollbar-thumb {
    background: #e8edf1;
}
.ck .ck-source-editing-area textarea::-webkit-scrollbar-thumb {
    background: #eff2f5;
}

.el-form-item.is-error .ck.ck-editor__editable:not(.ck-editor__nested-editable).ck-rounded-corners{
    border-color: #f56c6c;
}

.ck.ck-math-form {
    padding: 0.6em !important;
}

.ck.ck-math-form:focus {
    outline: none !important;
}

[dir=ltr] .ck.ck-math-form>:not(:first-child),[dir=rtl] .ck.ck-math-form>:not(:last-child) {
    margin-left: 0.6em !important;
}

@media screen and (max-width:600px) {
    .ck.ck-math-form {
        padding: 0 !important;
    }

    .ck.ck-math-form .ck-labeled-input {
        margin: 0.6em 0.6em 0 !important;
    }

    .ck.ck-math-form .ck-labeled-input .ck-input-text {
        min-width: 0 !important;;
        width: 100% !important;
    }

    .ck.ck-math-form .ck-label,.ck.ck-math-form .ck-math-view .ck-button,.ck.ck-math-form .ck-math-view .ck.ck-math-preview {
        margin: 0.6em 0.6em 0 !important;
    }

    .ck.ck-math-form>.ck-button {
        padding: 0.6em !important;
        margin-top: 0.6em !important;
        border-radius: 0 !important;
        border: 0 !important;
        border-top: 1px solid #c4c4c4 !important;
    }

    [dir=ltr] .ck.ck-math-form>.ck-button {
        margin-left: 0 !important;
    }

    [dir=ltr] .ck.ck-math-form>.ck-button:first-of-type {
        border-right: 1px solid #c4c4c4 !important;
    }

    [dir=rtl] .ck.ck-math-form>.ck-button {
        margin-left: 0 !important;
    }

    [dir=rtl] .ck.ck-math-form>.ck-button:last-of-type {
        border-right: 1px solid #c4c4c4 !important;
    }
}

html.dark {
    --ck-color-base-background: #15171C;
    --ck-color-base-border: #26272F;
    --ck-color-base-text: #F5F5F5;
    --ck-color-base-foreground: #1E2027;
    --ck-color-focus-border: #0A84FF;
    --ck-color-text: #F5F5F5;
    --ck-color-shadow-drop: rgba(0, 0, 0, 0.4);
    --ck-color-shadow-inner: rgba(0, 0, 0, 0.2);
    --ck-color-button-default-background: transparent;
    --ck-color-button-default-hover-background: rgba(16, 126, 255, 0.15);
    --ck-color-button-default-active-background: rgba(16, 126, 255, 0.2);
    --ck-color-button-on-background: rgba(16, 126, 255, 0.15);
    --ck-color-button-on-hover-background: rgba(16, 126, 255, 0.2);
    --ck-color-button-on-active-background: rgba(16, 126, 255, 0.25);
    --ck-color-button-on-color: #0A84FF;
    --ck-color-button-action-background: #0A84FF;
    --ck-color-button-action-hover-background: #0070E0;
    --ck-color-button-action-active-background: #005CC0;
    --ck-color-button-action-text: #fff;
    --ck-color-dropdown-panel-background: #15171C;
    --ck-color-dropdown-panel-border: #26272F;
    --ck-color-input-background: #15171C;
    --ck-color-input-border: #26272F;
    --ck-color-input-text: #F5F5F5;
    --ck-color-input-disabled-background: #1A1C22;
    --ck-color-input-disabled-border: #26272F;
    --ck-color-input-disabled-text: #9A9CAE;
    --ck-color-list-background: #15171C;
    --ck-color-list-button-hover-background: rgba(16, 126, 255, 0.15);
    --ck-color-list-button-on-background: rgba(16, 126, 255, 0.15);
    --ck-color-list-button-on-text: #0A84FF;
    --ck-color-panel-background: #15171C;
    --ck-color-panel-border: #26272F;
    --ck-color-toolbar-background: #1E2027;
    --ck-color-toolbar-border: #26272F;
    --ck-color-tooltip-background: #26272F;
    --ck-color-tooltip-text: #F5F5F5;
    --ck-color-engine-placeholder-text: #9A9CAE;
    --ck-color-link-default: #0A84FF;
    --ck-color-table-focused-cell-background: rgba(16, 126, 255, 0.1);
    --ck-color-color-grid-check-icon: #F5F5F5;
    --ck-color-widget-blurred-border: #26272F;
    --ck-color-widget-hover-border: #0A84FF;
    --ck-color-widget-editable-focus-background: #1E2027;

    .ck.ck-editor__main > .ck-editor__editable {
        background: #15171C !important;
        color: #F5F5F5 !important;
    }

    .ck.ck-editor__editable.ck-focused {
        border-color: #26272F !important;
    }

    .ck.ck-toolbar {
        background: #1E2027 !important;
        border-color: #26272F !important;

        .ck.ck-button {
            color: #F5F5F5 !important;

            &:not(.ck-disabled):hover {
                background: rgba(16, 126, 255, 0.15) !important;
            }

            &.ck-on {
                background: rgba(16, 126, 255, 0.15) !important;
                color: #0A84FF !important;
            }
        }
    }

    .ck.ck-splitbutton:hover > .ck-button,
    .ck.ck-splitbutton.ck-splitbutton_open > .ck-button {
        background: rgba(16, 126, 255, 0.15) !important;
        color: #F5F5F5 !important;
    }

    .ck.ck-toolbar__separator {
        background: #26272F !important;
    }

    .ck.ck-editor__editable .ck-placeholder::before {
        color: #9A9CAE !important;
    }

    .ck.ck-list__item .ck-button {
        color: #F5F5F5 !important;

        &:hover:not(.ck-disabled) {
            background: rgba(16, 126, 255, 0.15) !important;
        }
    }

    .ck.ck-list__separator {
        background: #26272F !important;
    }

    .ck-dropdown__panel {
        background: #15171C !important;
        border-color: #26272F !important;
    }

    .ck.ck-input {
        background: #15171C !important;
        color: #F5F5F5 !important;
        border-color: #26272F !important;
    }

    .ck.ck-labeled-field-view > .ck.ck-labeled-field-view__input-wrapper > .ck.ck-label {
        color: #9A9CAE !important;
    }

    .ck .ck-source-editing-area textarea {
        background: #15171C !important;
        color: #F5F5F5 !important;

        &::-webkit-scrollbar-thumb {
            background: #26272F !important;
        }
    }

    .ck.ck-balloon-panel {
        background: #15171C !important;
        border-color: #26272F !important;

        &::before, &::after {
            border-bottom-color: #26272F !important;
        }

        &[class*="arrow_s"]::before, &[class*="arrow_s"]::after {
            border-top-color: #26272F !important;
        }
    }

    .ck.ck-link-form,
    .ck.ck-link-actions {
        .ck-button {
            color: #F5F5F5 !important;

            &:not(.ck-disabled):hover {
                background: rgba(16, 126, 255, 0.15) !important;
            }
        }

        a.ck-link-actions__preview {
            color: #0A84FF !important;
        }
    }

    .ck.ck-color-grid__tile {
        border-color: #26272F !important;

        &:hover, &.ck-on {
            border-color: #F5F5F5 !important;
        }
    }

    .ck.ck-color-table .ck-color-table__remove-color {
        color: #F5F5F5 !important;

        &:hover {
            background: rgba(16, 126, 255, 0.15) !important;
        }
    }

    .ck.ck-insert-table-dropdown__grid .ck-insert-table-dropdown__label {
        color: #9A9CAE !important;
    }

    .ck.ck-insert-table-dropdown__grid .ck-insert-table-dropdown-grid-box {
        border-color: #26272F !important;

        &.ck-on {
            border-color: #0A84FF !important;
            background: rgba(16, 126, 255, 0.2) !important;
        }
    }

    .ck.ck-button.ck-disabled {
        color: #9A9CAE !important;
    }

    .ck.ck-reset_all, .ck.ck-reset_all * {
        color: #F5F5F5;
    }

    .ck.ck-editor__editable {
        [style*="color:hsl(0,0%,0%)"],
        [style*="color:hsl(0, 0%, 0%)"],
        [style*="color:hsl(0,0%,5%)"],
        [style*="color:hsl(0, 0%, 5%)"],
        [style*="color:hsl(0,0%,10%)"],
        [style*="color:hsl(0, 0%, 10%)"],
        [style*="color:#000000"],
        [style*="color: #000000"],
        [style*="color:#000"],
        [style*="color: #000;"],
        [style*="color:rgb(0,0,0)"],
        [style*="color:rgb(0, 0, 0)"],
        [style*="color: rgb(0, 0, 0)"],
        [style*="color:#1c1c1e"],
        [style*="color:#333"] {
            color: #F5F5F5 !important;
        }
    }
}

html.midnight {
    --ck-color-base-background: #1A1714;
    --ck-color-base-border: #2C2823;
    --ck-color-base-text: #EAE6E1;
    --ck-color-base-foreground: #241F1B;
    --ck-color-focus-border: #D4915C;
    --ck-color-text: #EAE6E1;
    --ck-color-button-default-hover-background: rgba(200, 134, 60, 0.15);
    --ck-color-button-default-active-background: rgba(200, 134, 60, 0.2);
    --ck-color-button-on-background: rgba(200, 134, 60, 0.15);
    --ck-color-button-on-hover-background: rgba(200, 134, 60, 0.2);
    --ck-color-button-on-active-background: rgba(200, 134, 60, 0.25);
    --ck-color-button-on-color: #D4915C;
    --ck-color-dropdown-panel-background: #1A1714;
    --ck-color-dropdown-panel-border: #2C2823;
    --ck-color-input-background: #1A1714;
    --ck-color-input-border: #2C2823;
    --ck-color-input-text: #EAE6E1;
    --ck-color-input-disabled-background: #201D19;
    --ck-color-input-disabled-border: #2C2823;
    --ck-color-input-disabled-text: #A9A39D;
    --ck-color-list-background: #1A1714;
    --ck-color-list-button-hover-background: rgba(200, 134, 60, 0.15);
    --ck-color-list-button-on-background: rgba(200, 134, 60, 0.15);
    --ck-color-list-button-on-text: #D4915C;
    --ck-color-panel-background: #1A1714;
    --ck-color-panel-border: #2C2823;
    --ck-color-toolbar-background: #241F1B;
    --ck-color-toolbar-border: #2C2823;
    --ck-color-tooltip-background: #2C2823;
    --ck-color-tooltip-text: #EAE6E1;
    --ck-color-engine-placeholder-text: #A9A39D;
    --ck-color-link-default: #D4915C;
    --ck-color-table-focused-cell-background: rgba(200, 134, 60, 0.1);
    --ck-color-color-grid-check-icon: #EAE6E1;
    --ck-color-widget-blurred-border: #2C2823;
    --ck-color-widget-hover-border: #D4915C;
    --ck-color-widget-editable-focus-background: #241F1B;

    .ck.ck-editor__main > .ck-editor__editable {
        background: #1A1714 !important;
        color: #EAE6E1 !important;
    }

    .ck.ck-editor__editable.ck-focused {
        border-color: #2C2823 !important;
    }

    .ck.ck-toolbar {
        background: #241F1B !important;
        border-color: #2C2823 !important;

        .ck.ck-button {
            color: #EAE6E1 !important;

            &:not(.ck-disabled):hover {
                background: rgba(200, 134, 60, 0.15) !important;
            }

            &.ck-on {
                background: rgba(200, 134, 60, 0.15) !important;
                color: #D4915C !important;
            }
        }
    }

    .ck.ck-splitbutton:hover > .ck-button,
    .ck.ck-splitbutton.ck-splitbutton_open > .ck-button {
        background: rgba(200, 134, 60, 0.15) !important;
        color: #EAE6E1 !important;
    }

    .ck.ck-toolbar__separator {
        background: #2C2823 !important;
    }

    .ck.ck-editor__editable .ck-placeholder::before {
        color: #A9A39D !important;
    }

    .ck.ck-list__item .ck-button {
        color: #EAE6E1 !important;

        &:hover:not(.ck-disabled) {
            background: rgba(200, 134, 60, 0.15) !important;
        }
    }

    .ck.ck-list__separator {
        background: #2C2823 !important;
    }

    .ck-dropdown__panel {
        background: #1A1714 !important;
        border-color: #2C2823 !important;
    }

    .ck.ck-input {
        background: #1A1714 !important;
        color: #EAE6E1 !important;
        border-color: #2C2823 !important;
    }

    .ck.ck-labeled-field-view > .ck.ck-labeled-field-view__input-wrapper > .ck.ck-label {
        color: #A9A39D !important;
    }

    .ck .ck-source-editing-area textarea {
        background: #1A1714 !important;
        color: #EAE6E1 !important;

        &::-webkit-scrollbar-thumb {
            background: #2C2823 !important;
        }
    }

    .ck.ck-balloon-panel {
        background: #1A1714 !important;
        border-color: #2C2823 !important;

        &::before, &::after {
            border-bottom-color: #2C2823 !important;
        }

        &[class*="arrow_s"]::before, &[class*="arrow_s"]::after {
            border-top-color: #2C2823 !important;
        }
    }

    .ck.ck-link-form,
    .ck.ck-link-actions {
        .ck-button {
            color: #EAE6E1 !important;

            &:not(.ck-disabled):hover {
                background: rgba(200, 134, 60, 0.15) !important;
            }
        }

        a.ck-link-actions__preview {
            color: #D4915C !important;
        }
    }

    .ck.ck-color-grid__tile {
        border-color: #2C2823 !important;

        &:hover, &.ck-on {
            border-color: #EAE6E1 !important;
        }
    }

    .ck.ck-color-table .ck-color-table__remove-color {
        color: #EAE6E1 !important;

        &:hover {
            background: rgba(200, 134, 60, 0.15) !important;
        }
    }

    .ck.ck-insert-table-dropdown__grid .ck-insert-table-dropdown__label {
        color: #A9A39D !important;
    }

    .ck.ck-insert-table-dropdown__grid .ck-insert-table-dropdown-grid-box {
        border-color: #2C2823 !important;

        &.ck-on {
            border-color: #D4915C !important;
            background: rgba(200, 134, 60, 0.2) !important;
        }
    }

    .ck.ck-button.ck-disabled {
        color: #A9A39D !important;
    }

    .ck.ck-reset_all, .ck.ck-reset_all * {
        color: #EAE6E1;
    }
}

html:not(.dark) {
    .ck.ck-editor__editable {
        [style*="color:hsl(0,0%,100%)"],
        [style*="color:hsl(0, 0%, 100%)"],
        [style*="color:hsl(0,0%,95%)"],
        [style*="color:hsl(0, 0%, 95%)"],
        [style*="color:hsl(0,0%,90%)"],
        [style*="color:hsl(0, 0%, 90%)"],
        [style*="color:#ffffff"],
        [style*="color: #ffffff"],
        [style*="color:#fff"],
        [style*="color: #fff;"],
        [style*="color:rgb(255,255,255)"],
        [style*="color:rgb(255, 255, 255)"],
        [style*="color: rgb(255, 255, 255)"],
        [style*="color:#f5f5f5"] {
            color: #1c1c1e !important;
        }
    }
}

</style>